import React, { useEffect, useState } from 'react';
import { FileCheck2, Mail, Save, UserCheck } from 'lucide-react';
import { useAtualizarCliente, useCliente, useContatoSugerido } from '../hooks';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import type { BillingRequirements } from '../api';
import { Textarea } from '../../../shared/components/Textarea';

interface Props {
  clientId: string;
}

/**
 * Aba "Faturamento" do cadastro do cliente (SPEC 02 §1 Fase F —
 * Cenário 14 e Exemplo 2).
 *
 * Duas seções:
 *  - `requiresPurchaseOrder` — flag que bloqueia OS/faturamento sem
 *    número de pedido (validada backend via ClientBillingGuardService).
 *  - `billingRequirements` — checklist estruturado (aprovação de
 *    medição, relatório de atividades, portal do cliente). Cada
 *    checkbox mapeia para uma chave do JSONB.
 *
 * Alterações vão pelo `PATCH /clients/:id` normal — não são campos
 * críticos (não passam por workflow de aprovação da Fase D).
 */
export const ClienteFaturamento: React.FC<Props> = ({ clientId }) => {
  const { data: cliente, isLoading } = useCliente(clientId);
  const atualizar = useAtualizarCliente();
  // SPEC 03 Fase C — quem responde por NF hoje? Considera afastamento
  // e substituição definitiva automaticamente.
  const sugestao = useContatoSugerido(clientId, 'nota_fiscal');

  const [requiresPO, setRequiresPO] = useState(false);
  const [aprovacao, setAprovacao] = useState(false);
  const [relatorio, setRelatorio] = useState(false);
  const [portalEnabled, setPortalEnabled] = useState(false);
  const [portalUrl, setPortalUrl] = useState('');
  const [outrasRaw, setOutrasRaw] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  // Hidrata a partir do cliente carregado (uma vez por change de id).
  useEffect(() => {
    if (!cliente) return;
    setRequiresPO(cliente.requiresPurchaseOrder);
    const req: BillingRequirements = cliente.billingRequirements ?? {};
    setAprovacao(Boolean(req.aprovacaoMedicao));
    setRelatorio(Boolean(req.relatorioAtividades));
    setPortalEnabled(Boolean(req.portalCliente?.enabled));
    setPortalUrl(req.portalCliente?.url ?? '');
    setOutrasRaw((req.outras ?? []).join('\n'));
  }, [cliente]);

  const showFeedback = (kind: 'ok' | 'err', msg: string) => {
    setFeedback({ kind, msg });
    setTimeout(() => setFeedback(null), 4000);
  };

  const handleSalvar = async () => {
    const outras = outrasRaw.split('\n').map((s) => s.trim()).filter(Boolean);
    const billing: BillingRequirements = {};
    if (aprovacao) billing.aprovacaoMedicao = true;
    if (relatorio) billing.relatorioAtividades = true;
    if (portalEnabled) billing.portalCliente = { enabled: true, url: portalUrl.trim() || undefined };
    if (outras.length > 0) billing.outras = outras;
    const payload = {
      requiresPurchaseOrder: requiresPO,
      billingRequirements: Object.keys(billing).length > 0 ? billing : null,
    };
    try {
      await atualizar.mutateAsync({ id: clientId, data: payload });
      showFeedback('ok', 'Regras de faturamento salvas.');
    } catch (err) {
      showFeedback('err', getFriendlyErrorMessage(err, 'Erro ao salvar.'));
    }
  };

  if (isLoading) return <div className="text-sm text-gray-500">Carregando faturamento…</div>;

  const inputCls =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="space-y-4" data-testid="cliente-faturamento">
      <div className="flex items-center gap-2">
        <FileCheck2 size={18} />
        <h3 className="text-lg font-semibold text-gray-900">Regras de faturamento</h3>
      </div>
      <p className="text-xs text-gray-500">
        Módulos de OS/faturamento validam estas regras antes de liberar operações (Cenário 14 e
        Exemplo 2 da SPEC 02).
      </p>

      {/*
        SPEC 03 Fase C — Responsável Financeiro sugerido para NF.
        Se o titular está afastado, o backend já devolve o substituto
        e a UI mostra o aviso "responde por Titular até DD/MM".
      */}
      <div
        className="rounded-md border border-blue-100 bg-blue-50/60 p-3 text-sm"
        data-testid="responsavel-nf-sugerido"
      >
        <div className="mb-1 flex items-center gap-1 text-xs uppercase tracking-wide text-blue-800">
          <UserCheck size={12} /> Responsável por Nota Fiscal
        </div>
        {sugestao.isLoading ? (
          <div className="text-xs text-gray-500">Buscando…</div>
        ) : !sugestao.data?.contact ? (
          <div className="text-sm text-yellow-800">
            Nenhum contato ativo do cliente responde por faturamento. Cadastre um contato com a
            responsabilidade "Responsável Faturamento".
          </div>
        ) : (
          <div>
            <div className="text-sm font-medium text-gray-900">
              {sugestao.data.contact.nome}
            </div>
            <div className="text-xs text-gray-600 flex items-center gap-1">
              <Mail size={12} />
              <a
                href={`mailto:${sugestao.data.contact.email}`}
                className="text-blue-700 hover:underline"
              >
                {sugestao.data.contact.email}
              </a>
              {sugestao.data.contact.telefone && (
                <span className="text-gray-500">· {sugestao.data.contact.telefone}</span>
              )}
            </div>
            {sugestao.data.contact.substitutoOf && (
              <div className="mt-1 rounded bg-yellow-100 px-2 py-1 text-xs text-yellow-900">
                Respondendo por <strong>{sugestao.data.contact.substitutoOf.nome}</strong>{' '}
                (afastado até {sugestao.data.contact.substitutoOf.endsAt}).
              </div>
            )}
          </div>
        )}
      </div>

      {feedback && (
        <div
          className={`rounded-md border p-3 text-sm ${
            feedback.kind === 'ok'
              ? 'border-green-200 bg-green-50 text-green-700'
              : 'border-red-200 bg-red-50 text-red-700'
          }`}
          data-testid="faturamento-feedback"
        >
          {feedback.msg}
        </div>
      )}

      <div className="rounded-md border border-gray-200 bg-white p-4">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            checked={requiresPO}
            onChange={(e) => setRequiresPO(e.target.checked)}
            className="mt-1"
            data-testid="faturamento-po"
          />
          <div>
            <div className="font-medium text-gray-900">Exige número de pedido de compra</div>
            <div className="text-xs text-gray-500">
              Quando marcado, OS e faturamento exigem informar o nº de PO antes de liberar.
            </div>
          </div>
        </label>
      </div>

      <div className="rounded-md border border-gray-200 bg-white p-4 space-y-3">
        <div className="text-sm font-medium text-gray-900">Exigências específicas</div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={aprovacao}
            onChange={(e) => setAprovacao(e.target.checked)}
            data-testid="faturamento-aprovacao"
          />
          Aprovação de medição
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={relatorio}
            onChange={(e) => setRelatorio(e.target.checked)}
            data-testid="faturamento-relatorio"
          />
          Envio de relatório de atividades
        </label>

        <div className="rounded border border-gray-200 bg-gray-50 p-3 space-y-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={portalEnabled}
              onChange={(e) => setPortalEnabled(e.target.checked)}
              data-testid="faturamento-portal"
            />
            Cadastro em portal próprio do cliente
          </label>
          {portalEnabled && (
            <label className="block text-xs">
              <span className="mb-1 block text-gray-700">URL do portal</span>
              <input
                type="url"
                value={portalUrl}
                onChange={(e) => setPortalUrl(e.target.value)}
                placeholder="https://portal-cliente.example.com"
                className={inputCls}
                data-testid="faturamento-portal-url"
              />
            </label>
          )}
        </div>

        <Textarea
          label="Outras exigências (uma por linha)"
          value={outrasRaw}
          onChange={(e) => setOutrasRaw(e.target.value)}
          rows={3}
          data-testid="faturamento-outras"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSalvar}
          disabled={atualizar.isPending}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="faturamento-salvar"
        >
          <Save size={16} /> {atualizar.isPending ? 'Salvando…' : 'Salvar regras de faturamento'}
        </button>
      </div>
    </div>
  );
};
